import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCubes,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faSync,
  faCheck,
  faExclamationTriangle,
  faSave,
  faToggleOn,
  faToggleOff,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import './Products.css';

const Products = () => {
  const { admin } = useDashboardAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    prix: '',
    categorie: '',
    image_url: '',
    is_active: true
  });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard-admin/services');
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      showNotification('error', 'Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const formatAmount = (amount) => {
    const formatted = parseFloat(amount || 0).toFixed(0);
    return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' FCFA';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/dashboard-admin/services/${editingProduct.id}`, formData);
        showNotification('success', 'Service mis à jour');
      } else {
        await api.post('/dashboard-admin/services', formData);
        showNotification('success', 'Service créé');
      }
      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      showNotification('error', 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nom: product.nom,
      description: product.description || '',
      prix: product.prix,
      categorie: product.categorie || '',
      image_url: product.image_url || '',
      is_active: product.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id, createdBy) => {
    if (createdBy !== admin.id && admin.role !== 'owner' && admin.role !== 'super_admin') {
      showNotification('error', 'Vous ne pouvez supprimer que vos propres services');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      return;
    }

    try {
      await api.delete(`/dashboard-admin/services/${id}`);
      showNotification('success', 'Service supprimé');
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      showNotification('error', error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      nom: '',
      description: '',
      prix: '',
      categorie: '',
      image_url: '',
      is_active: true
    });
  };

  const filteredProducts = products.filter(product =>
    product.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.categorie?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const canDelete = (product) => {
    return product.created_by === admin.id || admin.role === 'owner' || admin.role === 'super_admin';
  };

  return (
    <div className="services-page">
      {notification && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheck : faExclamationTriangle} />
          {notification.message}
        </div>
      )}

      <div className="page-header">
        <div>
          <h1><FontAwesomeIcon icon={faCubes} /> Services &amp; Produits</h1>
          <p>Catalogue des services proposés aux clients</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Nouveau Service
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Rechercher un service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn-refresh" onClick={loadProducts} disabled={loading}>
          <FontAwesomeIcon icon={faSync} spin={loading} />
        </button>
      </div>

      <div className="services-table-container">
        <table className="services-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Description</th>
              <th>Prix</th>
              <th>Catégorie</th>
              <th>Statut</th>
              <th>Créé par</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="loading-cell">Chargement...</td>
              </tr>
            ) : paginatedProducts.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-cell">
                  <FontAwesomeIcon icon={faCubes} size="2x" />
                  <p>Aucun service trouvé</p>
                </td>
              </tr>
            ) : (
              paginatedProducts.map(product => (
                <tr key={product.id}>
                  <td className="service-name">
                    <strong>{product.nom}</strong>
                  </td>
                  <td className="description-cell">
                    {product.description ? (
                      <span title={product.description}>
                        {product.description.length > 50 
                          ? product.description.substring(0, 50) + '...' 
                          : product.description}
                      </span>
                    ) : <span className="text-muted">-</span>}
                  </td>
                  <td className="price-cell">
                    {formatAmount(product.prix)}
                  </td>
                  <td>
                    {product.categorie ? (
                      <span className="category-badge">{product.categorie}</span>
                    ) : <span className="text-muted">-</span>}
                  </td>
                  <td>
                    <span className={`status-badge ${product.is_active ? 'status-success' : 'status-danger'}`}>
                      <FontAwesomeIcon icon={product.is_active ? faToggleOn : faToggleOff} />
                      {product.is_active ? ' Actif' : ' Inactif'}
                    </span>
                  </td>
                  <td className="creator-cell">
                    {product.creator_name || <span className="text-muted">-</span>}
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button className="btn-icon" onClick={() => handleEdit(product)} title="Modifier">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      {canDelete(product) && (
                        <button 
                          className="btn-icon btn-delete" 
                          onClick={() => handleDelete(product.id, product.created_by)} 
                          title="Supprimer"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <div className="pagination-info">
            Page {currentPage} sur {totalPages}
            <span className="pagination-total">({filteredProducts.length} services)</span>
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Modifier le service' : 'Nouveau service'}</h2>
              <button className="close-btn" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nom du service *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prix (FCFA) *</label>
                    <input
                      type="number"
                      step="1"
                      value={formData.prix}
                      onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                      placeholder="Ex: 500000"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Catégorie</label>
                    <input
                      type="text"
                      value={formData.categorie}
                      onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                      placeholder="Ex: Abonnement, Module..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>URL de l'image</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <span>Service actif</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  <FontAwesomeIcon icon={faSave} /> {editingProduct ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
